// apps/hyperseguros-web/src/components/Nav/MenuTrigger.tsx

'use client'

import React from 'react'
import { Box } from '@mui/material'

/** menu: two lines; close: an X; back: a "‹" chevron. */
export type TMenuTriggerState = 'menu' | 'close' | 'back'

type Props = {
  state: TMenuTriggerState
  label: string
  controlsId?: string
  onClick: () => void
}

const LINE_WIDTH = 18
const LINE_THICKNESS = 1.5
/** Half the gap between the two resting lines. */
const LINE_OFFSET = 3.5
/** Length of each chevron arm. */
const ARM_LENGTH = 12
/** Where the chevron's apex sits, from the left of the glyph box. */
const APEX_X = 3
const STEP = '110ms'
const MORPH = '240ms'
const EASE = 'cubic-bezier(0.4, 0, 0.6, 1)'

/**
 * apple.com-style menu trigger: two hairlines that slide together and then
 * rotate into an X. Each line has an outer element for the translate (and
 * length) and an inner one for the rotation so the open/close steps can run
 * in sequence — open moves first and rotates second, close rotates first
 * and moves second. Inside a section the same two lines shorten, meet at
 * the left and become an iOS back chevron in one motion.
 */
export default function MenuTrigger({
  state,
  label,
  controlsId,
  onClick,
}: Props) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      aria-expanded={state !== 'menu'}
      aria-controls={controlsId}
      onClick={onClick}
      sx={{
        width: '44px',
        height: '44px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        color: 'inherit',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        borderRadius: '22px',
        '&:focus-visible': {
          outline: '2px solid currentColor',
          outlineOffset: '-4px',
        },
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          position: 'relative',
          display: 'block',
          width: `${LINE_WIDTH}px`,
          height: `${LINE_OFFSET * 2 + LINE_THICKNESS}px`,
        }}
      >
        <Line state={state} direction={1} />
        <Line state={state} direction={-1} />
      </Box>
    </Box>
  )
}

// A 45° arm of ARM_LENGTH spans this much in x and y.
const ARM_REACH = (ARM_LENGTH / 2) * Math.SQRT1_2

function Line({
  state,
  direction,
}: {
  state: TMenuTriggerState
  direction: 1 | -1
}) {
  // direction 1 is the top line: it rests above centre and moves down.
  let width = LINE_WIDTH
  let left = 0
  let y = 0
  let angle = 0
  if (state === 'menu') {
    y = -direction * LINE_OFFSET
  } else if (state === 'close') {
    angle = direction * 45
  } else {
    // Both arms rotate about their centres, so each centre sits one reach
    // right of the apex and one reach above/below the line, and the arms'
    // left ends meet at the apex.
    width = ARM_LENGTH
    left = APEX_X + ARM_REACH - ARM_LENGTH / 2
    y = -direction * ARM_REACH
    angle = -direction * 45
  }

  // Sequencing only matters between menu and close; the chevron morphs in
  // one motion.
  const sequenced = state !== 'back'
  const duration = sequenced ? STEP : MORPH
  const moveDelay = state === 'menu' ? STEP : '0ms'
  const turnDelay = state === 'close' ? STEP : '0ms'

  return (
    <Box
      component="span"
      sx={{
        position: 'absolute',
        top: '50%',
        left: `${left}px`,
        width: `${width}px`,
        height: `${LINE_THICKNESS}px`,
        marginTop: `${-LINE_THICKNESS / 2}px`,
        transform: `translateY(${y}px)`,
        transition: `transform ${duration} ${EASE} ${moveDelay}, width ${MORPH} ${EASE}, left ${MORPH} ${EASE}`,
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'block',
          width: '100%',
          height: '100%',
          borderRadius: '1px',
          backgroundColor: 'currentColor',
          transform: `rotate(${angle}deg)`,
          transition: `transform ${duration} ${EASE} ${turnDelay}`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      />
    </Box>
  )
}
